import { useState, useRef, useCallback, useMemo, useEffect, type Dispatch, type SetStateAction, type MutableRefObject } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { HttpError } from '@/lib/http';
import { queryKeys } from '@/lib/query/keys';
import { isOnboardingComplete, type OnboardingState, type OnboardingAdvanceRequest } from '@xavior/contracts';
import { resolveConsentProvider, type OAuthConsentProvider } from '@/features/oauth/components/OAuthConsentModal';
import { connectCapabilityFor } from '@/features/oauth/lib/connect-capability';
import { connectProvider } from '@/features/oauth/lib/connect-provider';
import { useOnboardingDiscovery } from './useOnboardingDiscovery';
import type { QuickAction, ChatMessage } from '@/features/chat/hooks/useChat';
import type { ChatUIAction } from '@/lib/api/endpoints/chat';
import type { WorkspaceModule } from '@/lib/api/endpoints/workspaces';

// Mirrors the constant in ChatBox — both write to the same localStorage key.

function extractDisplayName(firstReply: string): string {
  return firstReply
    .replace(/^(i'm|im|i am|my name is|they call me|it's|its|hey,?\s*i'm)\s+/i, '')
    .replace(/[.,!].*$/, '')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function isHttpPayload(
  err: unknown,
  status: number,
  payload: string,
): err is HttpError & { payload: string } {
  return err instanceof HttpError && err.status === status && err.payload === payload;
}

export interface OnboardingManagerOptions {
  token: string | null;
  profile: {
    onboardingState?: string | null;
    // PDR 010 D3: target the post-plan connect at the SSO account (login_hint).
    loginProvider?: 'google' | 'microsoft' | null;
    email?: string;
  } | null;
  activeConversation: { id: string; name?: string } | null;
  workspaceId: string;
  queryClient: QueryClient;
  workspaceModuleCatalogRef: MutableRefObject<WorkspaceModule[]>;
  applyUiActionsRef: MutableRefObject<(actions: ChatUIAction[]) => void>;
  refreshSession: () => Promise<void>;
  refreshWorkspaceModuleCatalog: () => Promise<WorkspaceModule[]>;
  addAssistantMessage: (text: string, convId?: string, opts?: Record<string, unknown>) => void;
  appendUserMessage: (fn: (prev: ChatMessage[]) => ChatMessage[]) => void;
  confirmOAuthConsent: (provider: OAuthConsentProvider) => Promise<boolean>;
  showToast: (msg: string, type?: 'error' | 'success' | 'info') => void;
  clearPendingQuickActions: () => void;
  pendingQuickActions: QuickAction[];
  isStreaming: boolean;
  /** Programmatic chat-pane open — jumps the chat dock straight to 'open' (ChatDockContext). */
  expandChatPane: () => void;
}

export interface OnboardingManagerReturn {
  emailOnboardingCompleted: boolean;
  setEmailOnboardingCompleted: Dispatch<SetStateAction<boolean>>;
  emailOAuthHandledRef: MutableRefObject<boolean>;
  handleOAuthPopup: (kind: 'oauth.google.email' | 'oauth.microsoft.outlook') => Promise<boolean>;
  handleDemoLoad: () => Promise<void>;
  handleOpenPage: (parentPagesId: string, childModuleId: string) => void;
  focusOnboardingModuleByType: (moduleType: string) => Promise<void>;
  handleAdvanceDirective: (action: unknown) => void;
  quickActions: QuickAction[];
  discoveryStatus: string | null;
}

export function useOnboardingManager({
  token,
  profile,
  activeConversation,
  workspaceId,
  queryClient,
  workspaceModuleCatalogRef,
  applyUiActionsRef,
  refreshSession,
  refreshWorkspaceModuleCatalog,
  addAssistantMessage,
  appendUserMessage,
  confirmOAuthConsent,
  showToast,
  clearPendingQuickActions,
  pendingQuickActions,
  isStreaming,
  expandChatPane,
}: OnboardingManagerOptions): OnboardingManagerReturn {
  const [emailOnboardingCompleted, setEmailOnboardingCompleted] = useState(false);
  const [discoveryStatus, setDiscoveryStatus] = useState<string | null>(null);
  const onboardingAdvanceChainRef = useRef<Promise<void>>(Promise.resolve());
  // Owned here so the OAuth chip handler can claim the event before the
  // storage listener fires, preventing a duplicate agent turn.
  const emailOAuthHandledRef = useRef(false);
  // Idempotency guard — prevents the discovery advance from firing twice if
  // a poll tick races with itself or the hook effect restarts.
  const discoveryAdvancedRef = useRef(false);

  const ensureChatPaneVisible = useCallback(() => {
    expandChatPane();
  }, [expandChatPane]);

  const focusOnboardingModuleByType = useCallback(async (moduleType: string) => {
    ensureChatPaneVisible();
    const isVisible = (m: WorkspaceModule) => m.type === moduleType && !m.archivedAt;
    let target = workspaceModuleCatalogRef.current.find(isVisible);
    if (!target) {
      const refreshed = await refreshWorkspaceModuleCatalog();
      target = refreshed.find(isVisible);
    }
    if (!target) {
      console.warn('[onboarding] module focus failed; no module found', { moduleType });
      return;
    }
    applyUiActionsRef.current([{ type: 'module.focus', moduleId: target.id, skipInvalidation: true } as ChatUIAction]);
  }, [ensureChatPaneVisible, workspaceModuleCatalogRef, refreshWorkspaceModuleCatalog, applyUiActionsRef]);

  const handleOpenPage = useCallback((parentPagesId: string, childModuleId: string) => {
    try {
      window.sessionStorage.setItem(
        `xavior:pages-pending-child:${parentPagesId}`,
        JSON.stringify({ childModuleId, childSource: 'module', ts: Date.now() }),
      );
    } catch { /* sessionStorage unavailable */ }
    ensureChatPaneVisible();
    applyUiActionsRef.current([{ type: 'module.focus', moduleId: parentPagesId, skipInvalidation: true } as any]);
    window.dispatchEvent(new CustomEvent('xavior:pages-open-child', {
      detail: { parentPagesId, childModuleId, childSource: 'module' },
    }));
  }, [ensureChatPaneVisible, applyUiActionsRef]);

  const handleOAuthPopup = useCallback(async (kind: 'oauth.google.email' | 'oauth.microsoft.outlook'): Promise<boolean> => {
    if (kind.includes('microsoft')) {
      const { listProviders } = await import('@/lib/api/endpoints/integrations');
      const providerList = token ? await listProviders(token) : [];
      const msAvailable = providerList.some(
        (p: { id: string; hasServerCredentials?: boolean }) =>
          p.id === 'microsoft' && p.hasServerCredentials,
      );
      if (!msAvailable) return false;
    }
    const label = kind.includes('microsoft') ? 'Connect Outlook' : 'Connect Gmail';
    if (profile?.onboardingState === 'email_pitched' && activeConversation?.id) {
      appendUserMessage((prev: ChatMessage[]) => [
        ...prev,
        { id: `user-${Date.now()}`, role: 'user' as const, content: label, timestamp: new Date() },
      ]);
    }
    const [, provider, ...rest] = kind.split('.');
    const scopeKey = rest.join('.');
    // ADR 017 D4: name the capability; the backend resolves the scope union. No
    // hardcoded frontend scope map (provider-leakage boundary).
    const capability = connectCapabilityFor(provider, scopeKey);
    const consentProvider = resolveConsentProvider(kind);
    if (consentProvider) {
      const proceed = await confirmOAuthConsent(consentProvider);
      if (!proceed) return false;
    }
    // Pre-redirect side effects (the page unloads on connect): dismiss the email
    // welcome so the reload lands in the inbox, and signal any mounted EmailPanel.
    try { localStorage.setItem('email-welcome-dismissed', '1'); } catch { /* best-effort */ }
    window.dispatchEvent(new CustomEvent('xavior:email-welcome-dismiss'));
    // FLOW A (onboarding first connect, no integrationId → the backend keeps the
    // account picker). Same-window redirect; on return the reload refreshes the
    // session + onboarding state and useOnboardingDiscovery takes over (the
    // backend advances onboarding on the OAuth callback).
    // PDR 010 D3: when the user connects the SAME provider they signed in with
    // (server-side profile.loginProvider), pass their email as login_hint so the
    // grant targets that account — no account chooser, and (with the backend's
    // incremental no-forced-prompt path) no "signing back in" re-consent. For a
    // different provider, or magic-link (no loginProvider), omit login_hint.
    const loginHint =
      profile?.loginProvider === provider ? profile?.email : undefined;
    try {
      await connectProvider({ provider, authToken: token, capability, conversationId: activeConversation?.id, loginHint });
      return true; // redirect in flight — the reload drives post-connect state
    } catch {
      // fetch failed before redirect — stay on chat to retry
      return false;
    }
  }, [token, profile?.onboardingState, profile?.loginProvider, profile?.email, activeConversation?.id, appendUserMessage, confirmOAuthConsent]);

  const handleDemoLoad = useCallback(async () => {
    // demoSeed.loadDemoSeed routes through http.* — it sends X-Workspace-Id
    // automatically (the JWT no longer embeds workspace context, #2735/#2736)
    // and throws HttpError on a non-2xx, so a failed attempt and a network
    // throw are caught uniformly by the retry loop below.
    let loaded = false;
    let lastErr: unknown = null;
    for (let i = 1; i <= 3; i++) {
      try {
        await api.demoSeed.loadDemoSeed(token);
        loaded = true;
        break;
      } catch (err) { lastErr = err; }
      if (i < 3) await new Promise((r) => setTimeout(r, i * 600));
    }
    if (!loaded) {
      console.error('[onboarding] demo-seed load failed after 3 attempts:', lastErr);
      showToast('Demo load failed — tap the chip again to retry.', 'error');
      return;
    }
    try { localStorage.setItem('email-welcome-dismissed', '1'); } catch { /* best-effort */ }
    window.dispatchEvent(new CustomEvent('xavior:email-welcome-dismiss'));
    // Enter the progressive reveal/tour on the seeded demo data. The demo-seed
    // route fires the onboarding discovery pipeline server-side (demo mode), so
    // advancing to `email_connected` seeds the OnboardingBeats scan beat, which
    // polls discovery-status and plays the same reveal + acts a real connect would.
    // The tour advances `email_connected → completed` itself on finish.
    if (token && (profile?.onboardingState === 'email_pitched' || profile?.onboardingState === 'pending')) {
      try {
        await api.onboarding.advance(token, { to: 'email_connected' });
        await refreshSession();
      } catch (err) {
        console.error('[onboarding] demo.load advance to email_connected failed:', err);
      }
    }
    queryClient.invalidateQueries({
      predicate: (q) => {
        const key = JSON.stringify(q.queryKey);
        return key.includes('email') || key.includes('gmail') || key.includes('integration');
      },
    });
    queryClient.invalidateQueries({ queryKey: ['objects'] });
    queryClient.invalidateQueries({ queryKey: ['google-accounts'] });
    queryClient.invalidateQueries({ queryKey: ['modules'] });
    queryClient.invalidateQueries({ queryKey: ['demo-seed', 'status'] });
    setTimeout(() => { window.dispatchEvent(new CustomEvent('xavior:demo-data-loaded')); }, 1500);
  }, [token, workspaceId, profile?.onboardingState, refreshSession, showToast, queryClient]);

  const handleAdvanceDirective = useCallback((action: unknown) => {
    type AdvanceAction = {
      type: 'onboarding.advance' | 'onboarding.complete';
      to?: OnboardingState;
      name?: string;
      profession?: string;
    };
    const a = action as AdvanceAction;
    // 'onboarding.complete' always targets the terminal state. If the server is
    // not yet in a state that can transition to 'completed' (e.g. a race with the
    // OAuth callback), the 400 is caught below and we resync — no state-aware
    // remapping here, which kept the directive's contract simple and predictable.
    const to: OnboardingState = a.type === 'onboarding.complete' ? 'completed' : (a.to ?? 'completed');
    const body: OnboardingAdvanceRequest = { to };
    if (a.type === 'onboarding.advance') {
      if (a.name) body.name = extractDisplayName(a.name);
      if (a.profession) body.profession = a.profession;
    }
    onboardingAdvanceChainRef.current = onboardingAdvanceChainRef.current.then(async () => {
      try {
        await api.onboarding.advance(token, body);
        await refreshSession();
        if (to === 'completed' && token && workspaceId && activeConversation?.id && activeConversation.name === 'Welcome') {
          try {
            const displayName = a.name ? extractDisplayName(a.name) : null;
            const newTitle = displayName ? `Chat with ${displayName}` : 'Chat';
            await api.workspaces.updateConversation(token, workspaceId, activeConversation.id, { title: newTitle });
            queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
          } catch (err) {
            console.error('[onboarding] failed to rename Welcome conversation:', err);
          }
        }
      } catch (err: unknown) {
        if (isHttpPayload(err, 400, 'illegal_transition')) {
          await refreshSession();
        } else if (isHttpPayload(err, 400, 'invalid_identity_data')) {
          console.warn('[onboarding] invalid identity data, agent will retry:', err.payload);
          await refreshSession();
        } else {
          console.error('[onboarding] advance failed:', err);
        }
      }
    });
  }, [token, profile?.onboardingState, workspaceId, activeConversation?.id, activeConversation?.name, refreshSession, queryClient]);

  // Clear stale agent quick-action chips the moment onboarding completes.
  const prevOnboardingStateRef = useRef(profile?.onboardingState);
  useEffect(() => {
    const prev = prevOnboardingStateRef.current;
    const curr = profile?.onboardingState;
    prevOnboardingStateRef.current = curr;
    if (prev && !isOnboardingComplete(prev) && isOnboardingComplete(curr ?? null)) {
      clearPendingQuickActions();
    }
  }, [profile?.onboardingState, clearPendingQuickActions]);

  // Discovery messages — inject job-result messages into the chat stream.
  const handleDiscoveryShowMe = useCallback((pageId: string) => {
    const pagesModule = workspaceModuleCatalogRef.current.find(
      (m) => m.type === 'pages' && !m.archivedAt,
    );
    if (pagesModule?.id) {
      handleOpenPage(pagesModule.id, pageId);
    } else {
      applyUiActionsRef.current([{ type: 'module.focus', moduleId: pageId, skipInvalidation: true } as any]);
    }
  }, [handleOpenPage, workspaceModuleCatalogRef, applyUiActionsRef]);

  const handleDiscoveryAdvanceToCompleted = useCallback(async () => {
    if (!token) return;
    if (discoveryAdvancedRef.current) return;
    discoveryAdvancedRef.current = true;
    try {
      await api.onboarding.advance(token, { to: 'completed' });
      await refreshSession();
    } catch (err) {
      console.warn('[onboarding] advance to completed failed:', err);
      try { await refreshSession(); } catch { /* ignore */ }
    }
  }, [token, refreshSession]);

  useOnboardingDiscovery({
    // Disabled: the OnboardingBeats transcript (rendered in ChatBox during
    // email_connected) now drives the progressive reveal + advance-to-completed,
    // replacing this text-only narration. Kept wired (inactive) so the advance
    // callback + status plumbing stay referenced.
    active: false,
    token,
    conversationId: activeConversation?.id,
    addAssistantMessage,
    onAdvanceToCompleted: handleDiscoveryAdvanceToCompleted,
    onShowMe: handleDiscoveryShowMe,
    onSetStatus: setDiscoveryStatus,
  });

  // Filter agent quick-action chips through the onboarding gate.
  const quickActions = useMemo(() => {
    if (isStreaming) return [];
    if (profile?.onboardingState && !isOnboardingComplete(profile.onboardingState)) return [];
    if (emailOnboardingCompleted) {
      return pendingQuickActions.filter((a) => {
        if (!a.kind) return true;
        if (a.kind.startsWith('oauth.')) return false;
        if (a.kind === 'demo.load') return false;
        return true;
      });
    }
    return pendingQuickActions;
  }, [pendingQuickActions, isStreaming, emailOnboardingCompleted, profile?.onboardingState]);

  return {
    emailOnboardingCompleted,
    setEmailOnboardingCompleted,
    emailOAuthHandledRef,
    handleOAuthPopup,
    handleDemoLoad,
    handleOpenPage,
    focusOnboardingModuleByType,
    handleAdvanceDirective,
    quickActions,
    discoveryStatus,
  };
}
